import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { 
  ChevronLeft, ChevronRight, Star, MapPin, Phone, Globe, Instagram, 
  DollarSign, SquarePen, Trash2, Heart 
} from "lucide-react";
import { API } from "../constants";
import { useAuth } from "../context/AuthContext";
import { StarRating } from "../components/StarRating";
import { PriceRating } from "../components/PriceRating";
import { InteractiveHistogram } from "../components/InteractiveHistogram";

export const RestaurantDetailPage = () => {
  const { id } = useParams();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newReview, setNewReview] = useState({ 
    rating: 5, 
    service_rating: 3,
    ambiance_rating: 3,
    food_rating: 3,
    price_rating: 3,
    comment: "",
    avg_price_per_person: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [selectedRating, setSelectedRating] = useState(null);
  const [userReview, setUserReview] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchRestaurant();
    checkFavorite();
  }, [id]);

  useEffect(() => {
    // Check if user already has a review
    if (restaurant && user) {
      const existingReview = restaurant.reviews?.find(r => r.user_id === user.id);
      if (existingReview) {
        setUserReview(existingReview);
        setNewReview({
          rating: existingReview.rating,
          service_rating: existingReview.service_rating || 3,
          ambiance_rating: existingReview.ambiance_rating || 3,
          food_rating: existingReview.food_rating || 3,
          price_rating: existingReview.price_rating || 3,
          comment: existingReview.comment,
          avg_price_per_person: existingReview.avg_price_per_person || ""
        });
      }
    }
  }, [restaurant, user]);

  const fetchRestaurant = async () => {
    try {
      const res = await axios.get(`${API}/restaurants/${id}`);
      setRestaurant(res.data);
    } catch (err) {
      toast.error("Error al cargar restaurante");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const checkFavorite = async () => {
    try {
      const res = await axios.get(`${API}/favorites`);
      setIsFavorite(res.data.includes(id));
    } catch (err) {
      console.error("Error checking favorite");
    }
  };

  const toggleFavorite = async () => {
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${id}`);
        toast.success("Eliminado de favoritos");
      } else {
        await axios.post(`${API}/favorites/${id}`);
        toast.success("Añadido a favoritos");
      }
      setIsFavorite(!isFavorite);
    } catch (err) {
      toast.error("Error al actualizar favoritos");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("¿Eliminar este restaurante?")) return;
    try {
      await axios.delete(`${API}/restaurants/${id}`);
      toast.success("Restaurante eliminado");
      navigate("/");
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!newReview.comment.trim()) return;
    
    setSubmitting(true);
    try {
      if (userReview && isEditing) {
        // Update existing review
        await axios.put(`${API}/reviews/${userReview.id}`, {
          rating: newReview.rating,
          service_rating: newReview.service_rating,
          ambiance_rating: newReview.ambiance_rating,
          food_rating: newReview.food_rating,
          price_rating: newReview.price_rating,
          comment: newReview.comment,
          avg_price_per_person: newReview.avg_price_per_person ? parseFloat(newReview.avg_price_per_person) : null
        });
        toast.success("Opinión actualizada");
        setIsEditing(false);
      } else {
        // Create new review
        await axios.post(`${API}/reviews`, {
          restaurant_id: id,
          rating: newReview.rating,
          service_rating: newReview.service_rating,
          ambiance_rating: newReview.ambiance_rating,
          food_rating: newReview.food_rating,
          price_rating: newReview.price_rating,
          comment: newReview.comment,
          avg_price_per_person: newReview.avg_price_per_person ? parseFloat(newReview.avg_price_per_person) : null
        });
        toast.success("Opinión añadida");
      }
      fetchRestaurant();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar opinión");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    try {
      await axios.delete(`${API}/reviews/${reviewId}`);
      toast.success("Opinión eliminada");
      if (userReview?.id === reviewId) {
        setUserReview(null);
        setNewReview({ rating: 5, service_rating: 3, ambiance_rating: 3, food_rating: 3, price_rating: 3, comment: "", avg_price_per_person: "" });
      }
      fetchRestaurant();
    } catch (err) {
      toast.error("Error al eliminar opinión");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!restaurant) return null;

  const isOwner = restaurant.is_owner || restaurant.owner_id === user?.id;
  const canEdit = isOwner || user?.role === "admin";
  
  // Filter reviews by selected rating
  const filteredReviews = selectedRating
    ? restaurant.reviews?.filter(r => r.rating === selectedRating) || []
    : restaurant.reviews || [];

  return (
    <div className="min-h-screen pb-24" data-testid="restaurant-detail-page">
      {/* Hero Image */}
      <div className="relative h-64 md:h-80">
        <img
          src={restaurant.main_photo || "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800"}
          alt={restaurant.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 gradient-overlay" />
        
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 glass rounded-full p-2"
          data-testid="back-btn"
        >
          <ChevronLeft size={24} />
        </button>
        
        <div className="absolute top-4 right-4 flex gap-2">
          <button
            onClick={() => {
              const text = `🍽️ ${restaurant.name}\n📍 ${restaurant.address}, ${restaurant.city}${restaurant.province ? `, ${restaurant.province}` : ""}${restaurant.postal_code ? `, ${restaurant.postal_code}` : ""}\n${restaurant.phone ? `📞 ${restaurant.phone}\n` : ""}${restaurant.website ? `🌐 ${restaurant.website}\n` : ""}`;
              const url = window.location.href;
              window.open(`https://wa.me/?text=${encodeURIComponent(text + "\n" + url)}`, '_blank');
            }}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-green-500 text-white hover:bg-green-600 active:bg-green-700 transition-colors"
            data-testid="share-whatsapp-btn"
            title="Compartir por WhatsApp"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </button>
          {canEdit && (
            <>
              <button
                onClick={() => navigate(`/edit/${id}`)}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-black border border-zinc-500 text-zinc-300 hover:border-amber-500 hover:text-amber-500 transition-colors"
                data-testid="edit-btn"
                title="Editar"
              >
                <SquarePen size={16} />
              </button>
              <button
                onClick={handleDelete}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-black border border-zinc-500 text-zinc-300 hover:border-red-400 hover:text-red-400 transition-colors"
                data-testid="delete-btn"
                title="Eliminar"
              >
                <Trash2 size={16} />
              </button>
            </>
          )}
        </div>
        
        <div className="absolute bottom-2 left-4 right-4">
          <span className="cuisine-badge mb-0.5 inline-block text-xs">{restaurant.cuisine_type}</span>
          <div className="flex items-center gap-2">
            <h1 className="text-xl md:text-3xl font-bold">{restaurant.name}</h1>
            <button
              onClick={toggleFavorite}
              className="p-0 transition-transform hover:scale-110"
              data-testid="favorite-btn"
            >
              <Heart
                size={22}
                className={isFavorite ? "fill-red-500 text-red-500" : "text-white/60 hover:text-red-400"}
              />
            </button>
          </div>
          <div className="text-zinc-300 text-xs space-y-0">
            <p className="flex items-center gap-1"><MapPin size={12} className="flex-shrink-0" /> {restaurant.address}, {restaurant.city}{restaurant.province ? `, ${restaurant.province}` : ""}{restaurant.postal_code ? `, ${restaurant.postal_code}` : ""}</p>
            {restaurant.phone && <a href={`tel:${restaurant.phone}`} className="flex items-center gap-1 hover:text-amber-500"><Phone size={12} className="flex-shrink-0" /> {restaurant.phone}</a>}
            <div className="flex items-center gap-2">{restaurant.website && <a href={restaurant.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-amber-500"><Globe size={14} /> Web Site</a>}{restaurant.instagram && restaurant.instagram.trim() !== "" && <a href={`https://instagram.com/${restaurant.instagram.replace("@", "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center hover:text-amber-500"><Instagram size={14} /></a>}</div>
          </div>
        </div>
      </div>

      <main className="p-4 max-w-4xl mx-auto">
        {/* Scores - Averages from User Reviews */}
        <section className="glass rounded-2xl p-4 mb-6">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            <div className="text-center">
              <p className="text-zinc-400 text-[10px] font-semibold mb-1">Opiniones</p>
              <div className="flex items-center justify-center gap-0">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={11} className={i <= Math.round(restaurant.avg_rating || 0) ? "fill-amber-500 text-amber-500" : "text-zinc-600"} />
                ))}
              </div>
              <p className="text-[10px] text-zinc-300 mt-1">{restaurant.avg_rating?.toFixed(1) || "0"} <span className="text-zinc-500">({restaurant.review_count || 0})</span></p>
            </div>
            <div className="text-center">
              <p className="text-zinc-400 text-[10px] font-semibold mb-1">Precio</p>
              <div className="flex items-center justify-center gap-0">
                {[1, 2, 3, 4, 5].map((i) => (
                  <DollarSign key={i} size={11} className={i <= Math.round(restaurant.avg_price || 0) ? "text-emerald-500" : "text-zinc-600"} />
                ))}
              </div>
              <p className="text-[10px] text-zinc-300 mt-1">{restaurant.avg_price?.toFixed(1) || "0"} <span className="text-zinc-500">({restaurant.review_count || 0})</span></p>
            </div>
            <div className="text-center">
              <p className="text-zinc-400 text-[10px] font-semibold mb-1">Servicio</p>
              <div className="flex items-center justify-center gap-0">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={11} className={i <= Math.round(restaurant.avg_service || 0) ? "fill-amber-500 text-amber-500" : "text-zinc-600"} />
                ))}
              </div>
              <p className="text-[10px] text-zinc-300 mt-1">{restaurant.avg_service?.toFixed(1) || "0"} <span className="text-zinc-500">({restaurant.review_count || 0})</span></p>
            </div>
            <div className="text-center">
              <p className="text-zinc-400 text-[10px] font-semibold mb-1">Comida</p>
              <div className="flex items-center justify-center gap-0">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={11} className={i <= Math.round(restaurant.avg_food || 0) ? "fill-amber-500 text-amber-500" : "text-zinc-600"} />
                ))}
              </div>
              <p className="text-[10px] text-zinc-300 mt-1">{restaurant.avg_food?.toFixed(1) || "0"} <span className="text-zinc-500">({restaurant.review_count || 0})</span></p>
            </div>
            <div className="text-center">
              <p className="text-zinc-400 text-[10px] font-semibold mb-1">Ambiente</p>
              <div className="flex items-center justify-center gap-0">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={11} className={i <= Math.round(restaurant.avg_ambiance || 0) ? "fill-amber-500 text-amber-500" : "text-zinc-600"} />
                ))}
              </div>
              <p className="text-[10px] text-zinc-300 mt-1">{restaurant.avg_ambiance?.toFixed(1) || "0"} <span className="text-zinc-500">({restaurant.review_count || 0})</span></p>
            </div>
            <div className="text-center">
              <p className="text-zinc-400 text-[10px] font-semibold mb-1">Precio Medio Pax</p>
              <p className="text-amber-500 text-xs font-semibold mt-1">{restaurant.avg_price_per_person ? `${restaurant.avg_price_per_person}€` : "-"}</p>
            </div>
          </div>
          
          {/* Specialties Row */}
          {restaurant.specialties?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-zinc-800">
              <p className="text-zinc-400 text-xs font-semibold mb-2">Especialidades</p>
              <div className="flex flex-wrap gap-1">
                {restaurant.specialties.map((specialty) => (
                  <span
                    key={specialty}
                    className="px-1.5 py-0.5 rounded border border-amber-500/50 text-amber-500 text-[10px]"
                  >
                    {specialty}
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Histogram + Map Side by Side */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Histogram - Left */}
          <div className="glass rounded-2xl p-4">
            <InteractiveHistogram 
              histogram={restaurant.histogram || {}} 
              total={restaurant.review_count || 0}
              selectedRating={selectedRating}
              onSelectRating={setSelectedRating}
            />
          </div>
          
          {/* Map - Right */}
          <div className="glass rounded-2xl p-4">
            <div className="map-container h-40 rounded-xl overflow-hidden">
              <iframe
                title="Ubicación del restaurante"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(`${restaurant.address}, ${restaurant.city}, ${restaurant.country}`)}`}
              />
            </div>
          </div>
        </section>

        {/* Description */}
        {restaurant.description && (
          <section className="mb-6">
            <p className="text-xs font-semibold text-zinc-400 mb-3">Observaciones</p>
            <div className="glass rounded-2xl p-4">
              <p className="text-zinc-300 text-xs leading-relaxed">{restaurant.description}</p>
            </div>
          </section>
        )}

        {/* Photo Gallery Carousel */}
        {restaurant.dish_photos?.length > 0 && (
          <section className="mb-6">
            <div className="relative">
              <div className="flex gap-2 overflow-hidden">
                {restaurant.dish_photos.slice(photoIndex, photoIndex + 4).map((photo, i) => (
                  <motion.div
                    key={photoIndex + i}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="w-1/4 flex-shrink-0 aspect-square rounded-xl overflow-hidden"
                  >
                    <img src={photo} alt={`Plato ${photoIndex + i + 1}`} className="w-full h-full object-cover" />
                  </motion.div>
                ))}
              </div>
              
              {/* Navigation arrows */}
              {restaurant.dish_photos.length > 4 && (
                <>
                  {photoIndex > 0 && (
                    <button
                      onClick={() => setPhotoIndex(Math.max(0, photoIndex - 4))}
                      className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 glass rounded-full p-2 hover:bg-amber-500/20"
                      data-testid="photo-prev-btn"
                    >
                      <ChevronLeft size={20} />
                    </button>
                  )}
                  {photoIndex + 4 < restaurant.dish_photos.length && (
                    <button
                      onClick={() => setPhotoIndex(Math.min(restaurant.dish_photos.length - 4, photoIndex + 4))}
                      className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 glass rounded-full p-2 hover:bg-amber-500/20"
                      data-testid="photo-next-btn"
                    >
                      <ChevronRight size={20} />
                    </button>
                  )}
                </>
              )}
            </div>
            
            {/* Photo counter */}
            {restaurant.dish_photos.length > 4 && (
              <p className="text-center text-xs text-zinc-500 mt-2">
                {photoIndex + 1}-{Math.min(photoIndex + 4, restaurant.dish_photos.length)} de {restaurant.dish_photos.length}
              </p>
            )}
          </section>
        )}

        {/* Add/Edit Review Form */}
        {userReview && !isEditing ? (
          <section className="text-center py-4 mb-6">
            <p className="text-zinc-400 text-sm mb-3">Ya has compartido tu experiencia</p>
            <button
              onClick={() => setIsEditing(true)}
              className="px-6 py-2 bg-amber-500 text-black font-semibold rounded-xl btn-glow text-sm"
              data-testid="edit-review-btn"
            >
              Editar mi opinión
            </button>
          </section>
        ) : (
          <section className="glass rounded-2xl p-4 mb-6">
            <form onSubmit={handleSubmitReview}>
              <p className="text-xs font-semibold text-zinc-400 mb-3">
                {isEditing ? "Editar mi experiencia" : "Compartir mi experiencia"}
              </p>
              
              {/* Rating selectors grid */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                {/* General Rating */}
                <div className="text-center">
                  <p className="text-xs text-zinc-400 mb-1">General</p>
                  <div className="flex justify-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setNewReview({ ...newReview, rating: star })}
                      >
                        <Star
                          size={18}
                          className={star <= newReview.rating ? "fill-amber-500 text-amber-500" : "text-zinc-600"}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Price Rating */}
                <div className="text-center">
                  <p className="text-xs text-zinc-400 mb-1">Precio</p>
                  <div className="flex justify-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((price) => (
                      <button
                        key={price}
                        type="button"
                        onClick={() => setNewReview({ ...newReview, price_rating: price })}
                      >
                        <DollarSign
                          size={16}
                          className={price <= newReview.price_rating ? "text-emerald-500" : "text-zinc-600"}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Service Rating */}
                <div className="text-center">
                  <p className="text-xs text-zinc-400 mb-1">Servicio</p>
                  <div className="flex justify-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setNewReview({ ...newReview, service_rating: star })}
                      >
                        <Star
                          size={16}
                          className={star <= newReview.service_rating ? "fill-amber-500 text-amber-500" : "text-zinc-600"}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Food Rating */}
                <div className="text-center">
                  <p className="text-xs text-zinc-400 mb-1">Comida</p>
                  <div className="flex justify-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setNewReview({ ...newReview, food_rating: star })}
                      >
                        <Star
                          size={16}
                          className={star <= newReview.food_rating ? "fill-amber-500 text-amber-500" : "text-zinc-600"}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Ambiance Rating */}
                <div className="text-center">
                  <p className="text-xs text-zinc-400 mb-1">Ambiente</p>
                  <div className="flex justify-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setNewReview({ ...newReview, ambiance_rating: star })}
                      >
                        <Star
                          size={16}
                          className={star <= newReview.ambiance_rating ? "fill-amber-500 text-amber-500" : "text-zinc-600"}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="mb-3">
                <textarea
                  value={newReview.comment}
                  onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                  className="form-input min-h-[80px] text-sm"
                  placeholder="Comparte tu experiencia..."
                  data-testid="review-comment-input"
                />
              </div>
              
              <div className="mb-3">
                <label className="text-xs text-zinc-400 mb-1 block">Precio medio pax (€)</label>
                <input
                  type="number"
                  value={newReview.avg_price_per_person}
                  onChange={(e) => setNewReview({ ...newReview, avg_price_per_person: e.target.value })}
                  className="px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-zinc-100 text-xs w-24"
                  placeholder="35"
                  data-testid="avg-price-input"
                />
              </div>
              
              <div className="flex gap-2">
                {isEditing && (
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="flex-1 py-2.5 bg-zinc-700 text-white font-semibold rounded-xl text-sm"
                  >
                    Cancelar
                  </button>
                )}
                <button
                  type="submit"
                  disabled={submitting || !newReview.comment.trim()}
                  className="flex-1 py-2.5 bg-amber-500 text-black font-semibold rounded-xl btn-glow disabled:opacity-50 text-sm"
                  data-testid="submit-review-btn"
                >
                  {submitting ? "Guardando..." : isEditing ? "Actualizar" : "Publicar"}
                </button>
              </div>
            </form>
          </section>
        )}

        {/* Reviews List */}
        {filteredReviews.length > 0 && (
          <section>
            <div className="flex justify-between items-center mb-3">
              <p className="text-xs font-semibold text-zinc-400">
                {selectedRating ? `Opiniones de ${selectedRating} ★` : "Todas las opiniones"} ({filteredReviews.length})
              </p>
              <p className="text-zinc-400 text-xs font-normal">Creado por: <span className="text-amber-500">{restaurant.owner_name || "Usuario"}</span></p>
            </div>
            <div className="space-y-3">
              {filteredReviews.map((review) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="glass rounded-xl p-3"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium text-sm">{review.user_name}</p>
                      <p className="text-xs text-zinc-500">
                        {new Date(review.created_at).toLocaleDateString("es-ES")}
                      </p>
                    </div>
                    {review.user_id === user?.id && (
                      <button
                        onClick={() => handleDeleteReview(review.id)}
                        className="text-zinc-500 hover:text-red-400"
                        data-testid={`delete-review-${review.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  
                  {/* Review Ratings Grid */}
                  <div className="grid grid-cols-6 gap-2 mb-2 py-2 border-y border-zinc-800">
                    <div className="text-center">
                      <p className="text-[10px] text-zinc-500 mb-0.5">General</p>
                      <div className="flex justify-center">
                        <StarRating rating={review.rating} size={10} showValue={false} />
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-zinc-500 mb-0.5">Precio</p>
                      <div className="flex justify-center">
                        <PriceRating price={review.price_rating || 3} size={10} />
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-zinc-500 mb-0.5">Servicio</p>
                      <div className="flex justify-center">
                        <StarRating rating={review.service_rating || 3} size={10} showValue={false} />
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-zinc-500 mb-0.5">Comida</p>
                      <div className="flex justify-center">
                        <StarRating rating={review.food_rating || 3} size={10} showValue={false} />
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-zinc-500 mb-0.5">Ambiente</p>
                      <div className="flex justify-center">
                        <StarRating rating={review.ambiance_rating || 3} size={10} showValue={false} />
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-zinc-500 mb-0.5">Precio Medio Pax</p>
                      <p className="text-amber-500 text-[10px] font-semibold mt-0.5">{review.avg_price_per_person ? `${review.avg_price_per_person}€` : "-"}</p>
                    </div>
                  </div>
                  
                  <p className="text-zinc-300 text-sm">{review.comment}</p>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {filteredReviews.length === 0 && selectedRating && (
          <div className="text-center py-8 text-zinc-400">
            No hay opiniones de {selectedRating} estrellas
          </div>
        )}
      </main>
    </div>
  );
};

export default RestaurantDetailPage;
